import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Stack } from '@mui/material'
import { useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import CancelConfirmIconButtons from '@/features/site/components/CancelConfirmIconButtons'
import SiteNotificationPreferenceSettingsFormFields from '@/features/site/components/settings/notification_preferences/form/SiteNotificationPreferenceSettingsFormFields'
import { useInvalidateSiteQuery } from '@/features/site/hooks/useSiteQuery'
import { useUpdateSiteNotificationPreferencesMutation } from '@/features/site/hooks/useUpdateSiteNotificationPreferencesMutation'
import { getUpdateSiteNotificationPreferencesFormSchema } from '@/features/site/schemas'
import type { Site, UpdateSiteNotificationPreferencesForm } from '@/features/site/types/site'
import {
  convertNotificationPreferencesFormToNotificationPreferences,
  convertNotificationPreferencesToNotificationPreferencesForm,
} from '@/features/site/utils/siteForm'
import { errorHandler } from '@/utils/errorHandler'

type SiteSettingsFormProps = {
  site: Site
  isEmailEnabled: boolean | null
  isSmsEnabled: boolean | null
  onReset: () => void
  onSubmit: () => void
}

export const SiteNotificationPreferenceSettingsForm = ({
  site,
  onReset,
  onSubmit,
  isEmailEnabled,
  isSmsEnabled,
}: SiteSettingsFormProps) => {
  const { t } = useTranslation()
  const form = useForm({
    mode: 'onBlur',
    defaultValues: convertNotificationPreferencesToNotificationPreferencesForm(site.notificationPreferences),
    resolver: zodResolver(getUpdateSiteNotificationPreferencesFormSchema(t)),
  })
  const { updateSiteNotificationPreferences, isPending } = useUpdateSiteNotificationPreferencesMutation()
  const invalidateSiteQuery = useInvalidateSiteQuery()
  const { pushAlert } = useAlertContext()

  useEffect(() => {
    if (isSmsEnabled !== null) {
      form.setValue('notificationPreferences.isSmsEnabled', isSmsEnabled)

      // We need to trigger validation for the phone numbers when SMS checkbox change
      form.trigger('notificationPreferences.phoneNumbers.0')
    }
  }, [isSmsEnabled])

  useEffect(() => {
    if (isEmailEnabled !== null) {
      form.setValue('notificationPreferences.isEmailEnabled', isEmailEnabled)

      // We need to trigger validation for the email when Email checkbox change
      form.trigger('notificationPreferences.email')
    }
  }, [isEmailEnabled])

  async function handleSubmit(formData: UpdateSiteNotificationPreferencesForm) {
    try {
      await updateSiteNotificationPreferences({
        siteUuid: site.uuid,
        location: site.location!,
        payload: convertNotificationPreferencesFormToNotificationPreferences(formData)!,
      })
      await invalidateSiteQuery({ uuid: site.uuid })

      pushAlert({
        message: t('customer_settings.form.save_success'),
        severity: 'success',
      })

      onSubmit()
    } catch (err) {
      const error = errorHandler(err, t('customer_settings.form.save_error'))

      pushAlert({
        severity: 'error',
        message: error.message,
      })
    }
  }

  function handleReset() {
    onReset()

    form.reset(convertNotificationPreferencesToNotificationPreferencesForm(site.notificationPreferences))
  }

  const handleConfimClick = useCallback(() => {
    if (!form.formState.isValid) {
      pushAlert({
        severity: 'error',
        message: t('customer_settings.form.error.validation_error'),
      })
    }
  }, [form.formState.isValid])

  return (
    <FormProvider {...form}>
      <Stack
        noValidate
        component="form"
        direction="row"
        gap={2}
        justifyContent="flex-end"
        onReset={handleReset}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <Box flexGrow={1}>
          <SiteNotificationPreferenceSettingsFormFields />
        </Box>

        <CancelConfirmIconButtons disabled={isPending} onConfimClick={handleConfimClick} />
      </Stack>
    </FormProvider>
  )
}
